import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import DotLoader from '@svg/dot_loader';

import { IconButton, Collapse, Divider, Stack, Chip, alpha } from '@mui/material';
import { ExpandLess, ExpandMore, BuildOutlined } from '@mui/icons-material';
import { Loader } from '@common/hooks/useLoader';
import { Code } from '@common/Code';
import { OpenAIFunction } from '@common/hooks/useChatbot';

const ToolBox = styled.div`
  border: 1px solid ${({ theme }) => theme.line.light};
  border-radius: 8px;
  overflow: hidden;
`;

const ToolHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background-color: ${({ theme }) => alpha(theme.background.inverse, 0.02)};
`;

const ToolTitle = styled.div`
  font-weight: 500;
  font-size: 14px;
`;

const ToolArguments = styled.div`
  display: flex;
  align-items: center;
  border-top: 1px solid ${({ theme }) => theme.line.light};
`;

const ToolContent = styled.div`
  padding: 12px;
  font-size: 13px;
  white-space: pre-wrap;
  max-height: 400px;
  overflow-y: auto;
  word-break: break-word;

  ::-webkit-scrollbar {
    width: 14px;
    height: 14px;
  }

  ::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.scrollbar.track};
  }

  ::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.scrollbar.thumb};
  }

  pre {
    white-space: pre-wrap;
    word-break: break-word;
    font-size: 13px;
    padding: 0;
  }
`;

interface ChatToolProps {
  openTool: string | null;
  setOpenTool: React.Dispatch<React.SetStateAction<string | null>>;
  tool: OpenAIFunction;
}

export const ChatTool: React.FC<ChatToolProps> = ({ tool, openTool, setOpenTool }) => {
  const [expanded, setExpanded] = useState(openTool === tool.id);

  const toggleExpanded = () => {
    setExpanded(!expanded);
    if (openTool === tool.id) setOpenTool('noTool');
  };

  const parseArguments = (input: string) => {
    try {
      return JSON.parse(input || '');
    } catch (err) {
      return input;
    }
  };

  useEffect(() => {
    setExpanded(openTool === tool.id);
  }, [openTool]);

  const noArguments = !tool.input || tool.input === '' || tool.input.trim() === '{}';
  const isAssistantTool = Boolean(tool?.assistant_tool);

  return (
    <ToolBox>
      <ToolHeader>
        <Stack direction='row' spacing={1} alignItems='center' sx={{ paddingRight: '1rem' }}>
          <BuildOutlined fontSize='small' />
          <ToolTitle>{tool.action}</ToolTitle>
        </Stack>
        <Stack direction='row' spacing={1} alignItems='center'>
          {!tool.output && <DotLoader height={6} />}
          {isAssistantTool && <Chip label={tool.assistant_tool} size='small' />}
          <IconButton size='small' onClick={toggleExpanded}>
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Stack>
      </ToolHeader>
      <Collapse in={expanded}>
        {tool.input && !noArguments && (
          <ToolArguments>
            <Code code={parseArguments(tool.input)} language='json' />
          </ToolArguments>
        )}
        {tool.input && <Divider />}
        {!tool.output ? (
          <Stack direction='row' justifyContent='center' alignItems='center' sx={{ padding: '1rem' }}>
            <Loader />
          </Stack>
        ) : (
          <ToolContent>
            {typeof tool.output === 'string' ? (
              <pre>{tool.output}</pre>
            ) : (
              <Code code={parseArguments(tool.output)} language='json' />
            )}
          </ToolContent>
        )}
      </Collapse>
    </ToolBox>
  );
};
